import { createGlobalStyle, keyframes } from 'styled-components';

// ==================== ANIMATION KEYFRAMES ====================

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const wave = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const ripple = keyframes`
  0% {
    transform: scale(0, 0);
    opacity: 1;
  }
  20% {
    transform: scale(25, 25);
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(40, 40);
  }
`;

// ==================== GLOBAL STYLES ====================

const GlobalStyles = createGlobalStyle`
  /* CSS Reset */
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* ==================== ACCESSIBILITY ====================  */

  /* WCAG 2.1 AA Compliant Focus Indicators */
  *:focus-visible {
    outline: 3px solid #4a90e2;
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.3);
  }

  /* Enhanced focus for links */
  a:focus-visible {
    outline: 3px solid #4a90e2;
    outline-offset: 2px;
    border-radius: 4px;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.3);
  }

  /* Enhanced focus for buttons */
  button:focus-visible {
    outline: 3px solid #4a90e2;
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.3);
  }

  /* Skip to main content link for keyboard navigation */
  .skip-to-main {
    position: absolute;
    top: -40px;
    left: 0;
    background: #1b58a0;
    color: white;
    padding: 8px 16px;
    text-decoration: none;
    border-radius: 0 0 4px 0;
    z-index: 100;
    transition: top 0.2s ease;

    &:focus {
      top: 0;
    }
  }

  /* ==================== BASE STYLES ====================  */

  body {
    font-family: 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #f5f7fa;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    scroll-behavior: smooth;
    overflow-x: hidden;
  }

  #root {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* ==================== ANIMATION UTILITIES ====================  */

  .animate-fadeIn {
    animation: ${fadeIn} 0.4s ease-out both;
  }

  .animate-fadeInUp {
    animation: ${fadeInUp} 0.4s ease-out both;
  }

  .animate-slideInLeft {
    animation: ${slideInLeft} 0.4s ease-out both;
  }

  .animate-pulse {
    animation: ${pulse} 2s ease-in-out infinite;
  }

  .animate-shake {
    animation: ${shake} 0.5s ease-in-out;
  }

  .animate-spin {
    animation: ${spin} 1s linear infinite;
  }

  /* Stagger delays for list items */
  .stagger-1 { animation-delay: 50ms; }
  .stagger-2 { animation-delay: 100ms; }
  .stagger-3 { animation-delay: 150ms; }
  .stagger-4 { animation-delay: 200ms; }
  .stagger-5 { animation-delay: 250ms; }

  /* ==================== MATERIAL-UI OVERRIDES ====================  */

  .MuiTextField-root {
    background-color: #ffffff;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .MuiDialog-paper {
    border-radius: 12px !important;
    box-shadow: 0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12) !important;
  }

  .MuiDialogContent-root {
    border-bottom-left-radius: 12px !important;
    border-bottom-right-radius: 12px !important;
  }

  /* ==================== UTILITY CLASSES ====================  */

  .custom-box-shadow {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  .elevation-1 {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  .elevation-2 {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  }

  .elevation-3 {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.16);
  }

  /* Hover lift effect */
  .hover-lift {
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    }
  }

  /* Hover scale effect */
  .hover-scale {
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
  
  /* ==================== MAP COMPONENTS ====================  */

  .custom-marker-cluster {
    background: transparent;
    border: none;
  }

  .cluster-icon {
    background-color: #2166cd;
    color: white;
    border-radius: 50%;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
    font-size: 14px;
    box-shadow: 0 0 0 8px rgba(33, 102, 205, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 0 0 12px rgba(33, 102, 205, 0.3);
    }
  }

  /* ==================== DASHBOARD COMPONENTS ====================  */

  .dashboard-card {
    background-color: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    border-radius: 12px !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    cursor: pointer !important;
    pointer-events: auto !important;
    height: 80px !important;
    border: 1px solid rgba(0, 0, 0, 0.05);

    &:hover {
      transform: translateY(-4px) scale(1.02) !important;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12) !important;
      border-color: rgba(33, 102, 205, 0.2);
    }

    &:active {
      transform: translateY(-2px) scale(1.01) !important;
    }
  }

  .dashboard-card-content {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    height: 100% !important;
    padding: 8px 16px !important;
  }

  .dashboard-card-count {
    color: #2166cd !important;
    margin-right: 1px !important;
    font-size: 2.5rem !important;
    font-weight: 700 !important;
    line-height: 1;
    transition: transform 0.2s ease;

    .dashboard-card:hover & {
      transform: scale(1.05);
    }
  }

  .dashboard-card-type {
    font-size: 1.3rem !important;
    font-weight: 500;
  }

  .dashboard-card-icon {
    font-size: 30px !important;
    opacity: 0.8;
    transition: all 0.2s ease;

    .dashboard-card:hover & {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  /* ==================== SCROLLBAR STYLING ====================  */

  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
    transition: background 0.2s ease;

    &:hover {
      background: #a1a1a1;
    }
  }

  /* ==================== SELECTION STYLING ====================  */

  ::selection {
    background-color: rgba(33, 102, 205, 0.2);
    color: #1b58a0;
  }

  ::-moz-selection {
    background-color: rgba(33, 102, 205, 0.2);
    color: #1b58a0;
  }

  /* ==================== LOADING STATES ====================  */

  .loading-shimmer {
    position: relative;
    overflow: hidden;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: ${wave} 1.5s ease-in-out infinite;
  }

  /* ==================== RIPPLE EFFECT ====================  */

  @keyframes ripple {
    0% {
      transform: scale(0, 0);
      opacity: 1;
    }
    20% {
      transform: scale(25, 25);
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: scale(40, 40);
    }
  }
`;

export { gradientAnimation, fadeIn, fadeInUp, slideInLeft, pulse, shake, spin, wave };


export default GlobalStyles;